import { computed, ref, shallowRef, watch, type Ref } from 'vue';
import { fetchRoute, originLngLat, type Origin, type Route, type TravelMode } from './data/directions';
import type { LngLat, MapLocation } from './data/types';

type HereState = Extract<Origin, { kind: 'here' }>;

/**
 * Directions state for the location being viewed. The URL holds `dir` (travel mode) and
 * `from` (a location slug, or "here" for the user's position). Coordinates of the user's
 * position are never put in the URL.
 */
export function useDirections(
  /** Where the route ends (the destination's door); null when directions aren't open. */
  destination: Ref<LngLat | null>,
  mode: Ref<TravelMode | null>,
  from: Ref<string | null>,
  bySlug: Ref<Map<string, MapLocation>>,
) {
  // shallowRef: keep the coordinates a plain array (it's handed to mapbox-gl as-is).
  const here = shallowRef<HereState>({ kind: 'here', lngLat: null, status: 'locating' });
  let located = false;

  function locate() {
    if (!('geolocation' in navigator)) {
      here.value = { kind: 'here', lngLat: null, status: 'error', message: 'This browser can’t share your location. Search for a starting point instead.' };
      return;
    }
    here.value = { kind: 'here', lngLat: null, status: 'locating' };
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        located = true;
        here.value = { kind: 'here', lngLat: [pos.coords.longitude, pos.coords.latitude], status: 'ready' };
      },
      (err) => {
        here.value = {
          kind: 'here',
          lngLat: null,
          status: 'error',
          message:
            err.code === err.PERMISSION_DENIED
              ? 'Location access is blocked. Allow it in your browser settings, or search for a starting point.'
              : 'We couldn’t find your location. Try again, or search for a starting point.',
        };
      },
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 60_000 },
    );
  }

  // Ask for the position when "from=here" appears (including on page load), once per session
  // unless it failed.
  watch(
    from,
    (value) => {
      if (value === 'here' && (!located || here.value.status === 'error')) locate();
    },
    { immediate: true },
  );

  const origin = computed<Origin | null>(() => {
    if (!from.value) return null;
    if (from.value === 'here') return here.value;
    const location = bySlug.value.get(from.value.toLowerCase());
    return location ? { kind: 'location', location } : null;
  });

  const route = shallowRef<Route | null>(null);
  const routeStatus = ref<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const routeError = ref<string | null>(null);
  let controller: AbortController | undefined;

  const request = computed(() => {
    const start = originLngLat(origin.value);
    const end = destination.value;
    return mode.value && start && end ? { mode: mode.value, start, end } : null;
  });

  watch(
    () => (request.value ? JSON.stringify(request.value) : null),
    async () => {
      controller?.abort();
      const req = request.value;
      if (!req) {
        route.value = null;
        routeStatus.value = 'idle';
        routeError.value = null;
        return;
      }
      if (samePlace(req.start, req.end)) {
        route.value = null;
        routeStatus.value = 'error';
        routeError.value = 'You’re already there.';
        return;
      }
      controller = new AbortController();
      const { signal } = controller;
      routeStatus.value = 'loading';
      try {
        const result = await fetchRoute(req.mode, req.start, req.end, signal);
        if (signal.aborted) return;
        route.value = result;
        routeStatus.value = 'ready';
        routeError.value = null;
      } catch (err) {
        if (signal.aborted) return;
        route.value = null;
        routeStatus.value = 'error';
        routeError.value = err instanceof Error && err.message ? err.message : 'Couldn’t get directions.';
        console.error('[maps] directions failed', err);
      }
    },
    { immediate: true },
  );

  return { origin, route, routeStatus, routeError, retryLocate: locate };
}

function samePlace([a, b]: LngLat, [c, d]: LngLat): boolean {
  return Math.abs(a - c) < 1e-5 && Math.abs(b - d) < 1e-5;
}
